import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useGameSettings } from '../hooks/useGameSettings';
import { useActionHints } from '../hooks/useActionHints';
import { GameState } from '../types';
import { Language, t } from '../data/translations';
import { getCardDisplayNameForCard } from '../constants';
import { motion, AnimatePresence } from 'framer-motion';
import TutorialGhostArrow from './TutorialGhostArrow';
import { uiAnchorRegistry, UI_ANCHOR_IDS, getPreparedCardAnchorId } from '../services/uiAnchorRegistry';
import { CardColor } from '../types';

interface InstructorGuideProps {
    gameState: GameState | null;
    localPlayerId: number;
    language: Language;
    isPaused?: boolean;
}

const InstructorGuide: React.FC<InstructorGuideProps> = ({ gameState, localPlayerId, language, isPaused }) => {
    const { settings, updateSetting } = useGameSettings();
    const { hints } = useActionHints(gameState, localPlayerId);
    
    // Track when user closed the hint to avoid showing it endlessly in the same phase
    const [dismissedHintKeys, setDismissedHintKeys] = useState<Set<string>>(new Set());
    const [assetTracker, setAssetTracker] = useState({ key: '', index: 1 });

    // Listen for instructor reset event (e.g. from OptionsMenu)
    useEffect(() => {
        const handleReset = () => {
            setDismissedHintKeys(new Set());
        };
        window.addEventListener('instructor:resetMemory', handleReset);
        return () => window.removeEventListener('instructor:resetMemory', handleReset);
    }, []);

    // Reset when game advances so new hints appear
    useEffect(() => {
        if (!gameState) return;
        // Clear dismissed hints on phase or turn change, or when player's hand/prepared state changes.
        // This ensures that if a player closes a hint, then does something else (like preparing a card),
        // the instructor stays responsive and can show new hints.
        setDismissedHintKeys(new Set());
    }, [
        gameState?.round, 
        gameState?.phase, 
        gameState?.activations?.length, 
        gameState?.turnPlayerId,
        gameState?.players?.find(p => p.id === localPlayerId)?.prepared.length,
        gameState?.players?.find(p => p.id === localPlayerId)?.hand.length,
        gameState?.players?.find(p => p.id === localPlayerId)?.essence
    ]);

    const bestHint = hints.find(h => h.priority === 'AI_BEST');
    
    // Create a much more specific key so that if the hint changes its target or rotation, it's considered a new hint
    const currentHintKeyRaw = (gameState && bestHint) 
        ? `${gameState.round}-${gameState.phase}-${gameState.activations?.length || 0}-${bestHint.elementId}-${bestHint.actionType}-${bestHint.secondaryElementId || 'none'}-${bestHint.bestActionRotation ?? 'none'}` 
        : null;
        
    const currentHintKey = (currentHintKeyRaw && !dismissedHintKeys.has(currentHintKeyRaw)) ? currentHintKeyRaw : null;
    const isVisible = settings.instructorEnabled && !!gameState && !!bestHint && currentHintKey !== null && !isPaused;
    const lastDebugSnapshotRef = useRef<string>('');

    useEffect(() => {
        const snapshot = JSON.stringify({
            round: gameState?.round ?? null,
            phase: gameState?.phase ?? null,
            turnPlayerId: gameState?.turnPlayerId ?? null,
            hintsCount: hints.length,
            bestHintElementId: bestHint?.elementId ?? null,
            bestHintActionType: bestHint?.actionType ?? null,
            bestHintSecondary: bestHint?.secondaryElementId ?? null,
            bestHintRotation: bestHint?.bestActionRotation ?? null,
            currentHintKeyRaw,
            currentHintKey,
            dismissedSize: dismissedHintKeys.size,
            instructorEnabled: settings.instructorEnabled,
            isPaused: !!isPaused,
            isVisible,
        });
        if (snapshot !== lastDebugSnapshotRef.current) {
            lastDebugSnapshotRef.current = snapshot;
            console.log('[InstructorGuide][debug]', JSON.parse(snapshot));
        }
    }, [gameState?.round, gameState?.phase, gameState?.turnPlayerId, hints, bestHint, currentHintKeyRaw, currentHintKey, dismissedHintKeys, settings.instructorEnabled, isPaused, isVisible]);

    const handleClose = () => {
        if (currentHintKey) {
            setDismissedHintKeys(prev => {
                const next = new Set(prev);
                next.add(currentHintKey);
                return next;
            });
        }
    };

    useEffect(() => {
        if (isVisible && currentHintKey && currentHintKey !== assetTracker.key) {
             setAssetTracker(prev => ({
                 key: currentHintKey,
                 index: prev.key === '' ? 1 : ((prev.index % 14) + 1)
             }));
        }
    }, [isVisible, currentHintKey, assetTracker.key]);

    // Compute the textual representation for the UI so that we keep exit animations intact with accurate content
    const fullTextData = useMemo(() => {
        if (!gameState || !bestHint || !currentHintKey) return null;
        
        const player = gameState.players.find(p => p.id === localPlayerId);
        let elementName = bestHint.elementId;
        
        let overrideFullText = '';

        if (player) {
             if (bestHint.elementType === 'PREPARED_CARD') {
                 const pc = player.prepared.find(p => p.card.id.toString() === bestHint.elementId);
                 if (pc) {
                     elementName = getCardDisplayNameForCard(pc.card, pc.rotation, language as any);
                     // Micro-step: Flip the card if it's currently on the wrong side
                     if (bestHint.bestActionRotation !== undefined && pc.rotation !== bestHint.bestActionRotation) {
                         const targetSideName = getCardDisplayNameForCard(pc.card, bestHint.bestActionRotation, language as any);
                         overrideFullText = t('instructor.flip_first', language)
                            .replace('{elementName}', elementName)
                            .replace('{targetSideName}', targetSideName);
                     }
                 }
             } else if (bestHint.elementType === 'HAND_CARD' || bestHint.elementType === 'SPELL') {
                 const card = player.hand.find(c => c.id.toString() === bestHint.elementId);
                 if (card) {
                     elementName = getCardDisplayNameForCard(card, 0, language as any);
                     if (bestHint.actionType === 'PREPARE_CARD') {
                         overrideFullText = t('instructor.drag_to_prepare', language).replace('{elementName}', elementName);
                     }
                 }
             }
        }
        
        // Translation fallback
        if (bestHint.actionType === 'END_DAY' || bestHint.elementId === 'end-day-button') elementName = t('ui.endDay', language);
        if (bestHint.actionType === 'PASS' || bestHint.elementId === 'pass-button') elementName = t('ui.pass', language);
        if (bestHint.actionType === 'ACCEPT_ACTIVATION' || bestHint.elementId === 'accept-button') elementName = t('ui.accept', language);
        if (bestHint.actionType === 'ASTRID_BLAST') elementName = t('hero.ASTRID.name', language);
        if (bestHint.elementId === 'essence-mode-button') elementName = t('term.essence', language);
        if (bestHint.elementId === 'mage-spells-button') elementName = t('ui.spells', language);
        if (bestHint.elementId === 'capture-tower-button') elementName = t('ui.acquireTower', language);
        if (bestHint.elementId.startsWith('tower-')) {
             const colorCode = bestHint.elementId.replace('tower-', '');
             elementName = t(`color.${colorCode.toLowerCase()}` as any, language);
        }
        
        // Special phase text overrides
        if (bestHint.actionType === 'CHOOSE_LOCKED_CARD_RETURN') {
            overrideFullText = t('instructor.chooseLockedCardReturn', language).replace('{elementName}', elementName);
        } else if (bestHint.actionType === 'CHOOSE_BURGLARY_CARD') {
            overrideFullText = t('instructor.chooseBurglaryCard', language).replace('{elementName}', elementName);
        } else if (bestHint.actionType === 'CHOOSE_CHROMABOW_COLOR' || bestHint.actionType === 'CHOOSE_HIBERNATION_COLOR') {
            const colorMap: Record<string, string> = {
                'Red': t('color.red', language),
                'Black': t('color.black', language),
                'Green': t('color.green', language),
                'Yellow': t('color.yellow', language),
                'Purple': t('color.purple', language),
                'Pink': t('color.pink', language),
            };
            const colorCode = bestHint.elementId.split('-').pop() || '';
            const colorName = colorMap[colorCode] || colorCode;
            overrideFullText = t('instructor.chooseColor', language).replace('{colorName}', colorName);
        } else if (bestHint.actionType === 'CHOOSE_PURIFICATION_TARGET') {
            const colorCode = bestHint.elementId.replace('tower-', '');
            const colorMap: Record<string, string> = {
                'Red': t('color.red', language),
                'Black': t('color.black', language),
                'Green': t('color.green', language),
                'Yellow': t('color.yellow', language),
                'Purple': t('color.purple', language),
                'Pink': t('color.pink', language),
            };
            const colorName = colorMap[colorCode] || colorCode;
            overrideFullText = t('instructor.choosePurificationTarget', language).replace('{colorName}', colorName);
        } else if (bestHint.actionType === 'ASTRID_BLAST') {
            overrideFullText = t('instructor.astrid_blast_hint', language);
        } else if (bestHint.actionType === 'PURCHASE_TOWER') {
            overrideFullText = t('instructor.purchase_tower_hint', language).replace('{colorName}', elementName);
        }
        
        // Generate prediction
        let predictionKey = 'instructor.pred.default';
        switch (bestHint.actionType) {
            case 'PREPARE_CARD': predictionKey = 'instructor.pred.prepare_card'; break;
            case 'ATTEMPT_ACTIVATION': predictionKey = 'instructor.pred.attempt_activation'; break;
            case 'COUNTER_ACTIVATION': predictionKey = 'instructor.pred.counter_activation'; break;
            case 'END_DAY': predictionKey = 'instructor.pred.end_day'; break;
            case 'PASS': predictionKey = 'instructor.pred.pass'; break;
            case 'PURCHASE_TOWER': predictionKey = 'instructor.pred.purchase_tower'; break;
            case 'MAGE_CAST_SPELL': predictionKey = 'instructor.pred.mage_cast_spell'; break;
            case 'ACCEPT_ACTIVATION': predictionKey = 'instructor.pred.accept_activation'; break;
            case 'TOGGLE_ESSENCE_MODE': predictionKey = 'instructor.pred.toggle_essence_mode'; break;
            case 'TOGGLE_ESSENCE_MODE_OFF': predictionKey = 'instructor.pred.toggle_essence_mode_off'; break;
            case 'START_TOWER_PURCHASE': predictionKey = 'instructor.pred.start_tower_purchase'; break;
            case 'DISCARD_STASH': predictionKey = 'instructor.pred.discard_stash'; break;
            case 'DISCARD_HAND_FOR_ESSENCE': predictionKey = 'instructor.pred.discard_hand_for_essence'; break;
        }
        const prediction = t(predictionKey as any, language);

        let fullText = overrideFullText;
        if (!fullText) {
            const hasTarget = !!bestHint.secondaryElementId;
            const key = hasTarget ? 'instructor.tryPlayingTarget' : 'instructor.tryPlaying';
            fullText = t(key as any, language)
                .replace(/{cardName}/g, `[${elementName}]`)
                .replace('{prediction}', prediction);

            if (hasTarget && (bestHint.actionType === 'ATTEMPT_ACTIVATION' || bestHint.actionType === 'MAGE_CAST_SPELL' || bestHint.actionType === 'COUNTER_ACTIVATION')) {
                fullText += ' ' + t('instructor.drag_arrow_hint', language);
            }
        }
            
        if (!overrideFullText && ['BUTTON', 'TOWER', 'SLOT'].includes(bestHint.elementType)) {
            fullText = t('instructor.tryClicking', language)
                .replace('{elementName}', `[${elementName}]`)
                .replace('{prediction}', prediction);
        }

        return {
            fullText,
            targetIds: [bestHint.elementId, ...(bestHint.secondaryElementId ? [bestHint.secondaryElementId] : [])]
        };
    }, [gameState, bestHint, currentHintKey, language, localPlayerId]);

    // Calculate coordinates for the tutorial ghost arrow demonstration
    const ghostCoords = useMemo(() => {
        if (!isVisible || !bestHint || !bestHint.secondaryElementId) return null;
        
        // Only trigger for specific action types that use targeting arrow mechanics
        const targetingActions = ['ATTEMPT_ACTIVATION', 'MAGE_CAST_SPELL', 'ASTRID_BLAST', 'COUNTER_ACTIVATION'];
        if (!targetingActions.includes(bestHint.actionType || '')) return null;

        let startId = bestHint.elementId;
        if (bestHint.elementType === 'PREPARED_CARD') {
            startId = getPreparedCardAnchorId(bestHint.elementId);
        } else if (bestHint.actionType === 'ASTRID_BLAST') {
            startId = 'astrid-arrows-button';
        }
        
        let targetId = bestHint.secondaryElementId;
        if (targetId?.startsWith('avatar-')) {
            const idNum = parseInt(targetId.replace('avatar-', ''), 10);
            targetId = (idNum === localPlayerId) ? UI_ANCHOR_IDS.localAvatar : UI_ANCHOR_IDS.opponentAvatar;
        }
        
        // Attempt to get coordinates from registry, with fallback to direct DOM for special cases
        const getRect = (id: string) => uiAnchorRegistry.getRect(id) || document.getElementById(id)?.getBoundingClientRect();
        
        const startRect = getRect(startId);
        const targetRect = getRect(targetId);
        
        if (!startRect || !targetRect) return null;
        
        return {
            start: { x: startRect.left + startRect.width / 2, y: startRect.top + startRect.height / 2 },
            end: { x: targetRect.left + targetRect.width / 2, y: targetRect.top + targetRect.height / 2 },
            color: bestHint.cardColor || CardColor.Red
        };
    }, [isVisible, bestHint]);

    return (
        <div className="fixed inset-0 z-[100] pointer-events-none">
            <AnimatePresence mode="wait">
                {isVisible && fullTextData && currentHintKey ? (
                    <motion.div 
                        key={currentHintKey}
                        className="absolute inset-0 z-[100] pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Tutorial Ghost Arrow Demonstration */}
                        {ghostCoords && (
                            <TutorialGhostArrow 
                                start={ghostCoords.start}
                                end={ghostCoords.end}
                                color={ghostCoords.color}
                            />
                        )}
                        {/* Spotlight background uses a simple fade out */}
                        {/* <div className="absolute inset-0">
                            <SpotlightMask targetIds={fullTextData.targetIds} />
                        </div> */}
                        
                        <div className="absolute top-[2vh] left-1/2 -translate-x-1/2 w-[520px] max-w-[90vw] flex items-end">
                            {/* The Instructor Character Container */}
                            <div className="relative w-[120px] h-[160px] shrink-0 pointer-events-none mr-[-40px]">
                                <motion.img 
                                    src={`/assets/instructor/${assetTracker.index}.png`}
                                    alt="Instructor"
                                    className="absolute bottom-0 right-0 w-[180px] max-w-none h-auto object-contain origin-bottom-right drop-shadow-[0_10px_20px_rgba(0,0,0,0.6)]"
                                    initial={{ x: 100, y: 70, rotate: 0, scale: 0.8, zIndex: -10 }}
                                    animate={{ 
                                        x: [100, -30, 0], 
                                        y: [70, -40, 0], 
                                        rotate: [0, -45, -30],
                                        scale: [0.8, 1.1, 1],
                                        // Once it slides to the left (-30px away), it pops back on top (zIndex 20)
                                        zIndex: [-10, 20, 20]
                                    }}
                                    transition={{ 
                                        duration: 0.6, 
                                        ease: "easeInOut",
                                        times: [0, 0.5, 1]
                                    }}
                                />
                            </div>

                            {/* The Dialog Box */}
                            <motion.div 
                                className={`bg-[#1e140f]/95 backdrop-blur-xl border-2 border-[#8b5a2b]/80 rounded-xl p-6 shadow-[0_15px_50px_rgba(0,0,0,0.8),inset_0_0_30px_rgba(139,90,43,0.15)] flex flex-col flex-grow z-10 relative min-h-[160px] pl-[50px] font-sans ${isPaused ? 'pointer-events-none' : 'pointer-events-auto'}`}
                                initial={{ y: 20, scale: 0.95, opacity: 0 }}
                                animate={{ y: 0, scale: 1, opacity: 1, transition: { delay: 0.1 } }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                            >
                                <div className="flex items-center gap-3 mb-4 border-b border-[#8b5a2b]/30 pb-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-[#1e140f] font-bold text-2xl shadow-[0_0_15px_rgba(251,191,36,0.5)] border border-amber-200">
                                        !
                                    </div>
                                    <h3 className="text-amber-400 font-extrabold text-xl uppercase tracking-wider drop-shadow-md">
                                        {t('instructor.title', language)}
                                    </h3>
                                </div>
                                
                                <div className="flex-grow relative min-h-[50px] overflow-hidden -mx-2 px-2">
                                    <p className="text-amber-50/90 text-lg font-medium leading-relaxed mb-4 tracking-wide relative w-full">
                                        {fullTextData.fullText}
                                    </p>
                                </div>
                                
                                <div className="flex justify-between items-center pt-3 mt-auto">
                                    <button 
                                        onClick={() => updateSetting('instructorEnabled', false)}
                                        className="px-4 py-2 bg-transparent hover:bg-black/20 text-[#a38060] hover:text-amber-500 font-medium rounded transition-colors text-sm border border-transparent hover:border-[#8b5a2b]/50 pointer-events-auto"
                                    >
                                        {t('instructor.turnOff', language)}
                                    </button>
                                    
                                    <button 
                                        onClick={handleClose}
                                        className="px-6 py-2 bg-gradient-to-b from-[#8b5a2b] to-[#5c3a1a] hover:from-[#a06832] hover:to-[#6d4520] border border-[#a06832] text-amber-50 font-bold rounded shadow-[0_6px_15px_rgba(0,0,0,0.5)] transition-all transform hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.6)] pointer-events-auto"
                                    >
                                        {t('instructor.close', language)}
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
};

/*
const SpotlightMask: React.FC<{ targetIds: string[] }> = ({ targetIds }) => {
    const [rects, setRects] = useState<DOMRect[]>([]);

    useEffect(() => {
        const updateRects = () => {
             const newRects = targetIds.map(targetId => {
                 let el = document.getElementById(targetId);
                 
                 if (!el) {
                     if (targetId === 'pass-button') el = document.getElementById('btn-pass') || document.querySelector('[data-id="pass-button"]');
                     if (targetId === 'end-day-button') el = document.getElementById('btn-end-day') || document.querySelector('[data-id="end-day-button"]');
                     if (targetId === 'accept-button') el = document.getElementById('btn-accept') || document.querySelector('[data-id="accept-button"]');
                     if (targetId === 'essence-mode-button') el = document.getElementById('btn-essence-mode') || document.querySelector('[data-id="essence-mode"]');
                 }
                 
                 return el ? el.getBoundingClientRect() : null;
             }).filter(Boolean) as DOMRect[];
             
             setRects(newRects);
        };
        
        updateRects();
        const interval = setInterval(updateRects, 100); // Fast interval to catch animations
        window.addEventListener('resize', updateRects);
        window.addEventListener('scroll', updateRects, true);
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', updateRects);
            window.removeEventListener('scroll', updateRects, true);
        };
    }, [targetIds.join(',')]);

    // Full dark overlay if element is not found
    if (rects.length === 0) {
        return <div className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-none transition-all duration-500" />;
    }

    return (
        <>
            <svg className="w-0 h-0 absolute">
                <defs>
                    <mask id="spotlight-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
                        <rect width="100%" height="100%" fill="white" />
                        {rects.map((rect, i) => {
                            const padding = 25;
                            const cx = rect.left + rect.width / 2;
                            const cy = rect.top + rect.height / 2;
                            const radius = Math.max(rect.width + padding * 2, rect.height + padding * 2) / 2;
                            return <circle key={i} cx={cx} cy={cy} r={radius} fill="black" filter="url(#softEdge)" />;
                        })}
                    </mask>
                    <filter id="softEdge">
                        <feGaussianBlur stdDeviation="15" />
                    </filter>
                </defs>
            </svg>
            <div 
                className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-none transition-all duration-300"
                style={{ maskImage: 'url(#spotlight-mask)', WebkitMaskImage: 'url(#spotlight-mask)' }}
            />
        </>
    );
};
*/

export default InstructorGuide;
